// Handlebars-compatible subset (spec 08 §3): {{path}}, {{#if path}}…{{else}}…{{/if}}, {{#unless path}}…{{/unless}}.
// HTML escaping is on by default. Triple-stash (raw HTML) is deliberately unsupported.
import {
  decodeAttr,
  htmlTrim,
  isUrlAttr,
  markupAllowlistError,
  tokenize,
} from "./htmlTokens.ts";
export type TemplateNode =
  | { t: "text"; v: string }
  | { t: "var"; path: string }
  | {
      t: "if";
      path: string;
      neg: boolean;
      then: TemplateNode[];
      else: TemplateNode[];
    };
type IfNode = Extract<TemplateNode, { t: "if" }>;

export class TemplateSyntaxError extends Error {
  constructor(
    readonly code: string,
    readonly at: number,
  ) {
    super(`${code}@${at}`);
  }
}

const PATH = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;

export function parseTemplate(src: string): TemplateNode[] {
  const root: TemplateNode[] = [];
  const stack: { node: IfNode; inElse: boolean; kind: "if" | "unless" }[] = [];
  const out = (): TemplateNode[] => {
    const top = stack.at(-1);
    if (!top) return root;
    return top.inElse ? top.node.else : top.node.then;
  };
  let i = 0;
  while (i < src.length) {
    const open = src.indexOf("{{", i);
    if (open === -1) {
      out().push({ t: "text", v: src.slice(i) });
      break;
    }
    if (open > i) out().push({ t: "text", v: src.slice(i, open) });
    if (src.startsWith("{{{", open))
      throw new TemplateSyntaxError("triple_stash", open);
    const close = src.indexOf("}}", open + 2);
    if (close === -1) throw new TemplateSyntaxError("unclosed_tag", open);
    const inner = src.slice(open + 2, close).trim();
    i = close + 2;
    if (inner.startsWith("#if ") || inner.startsWith("#unless ")) {
      const kind = inner.startsWith("#if ") ? "if" : "unless";
      const path = inner.slice(kind === "if" ? 4 : 8).trim();
      if (!PATH.test(path)) throw new TemplateSyntaxError("bad_path", open);
      const node: IfNode = {
        t: "if",
        path,
        neg: kind === "unless",
        then: [],
        else: [],
      };
      out().push(node);
      stack.push({ node, inElse: false, kind });
    } else if (inner === "else") {
      const top = stack.at(-1);
      if (!top || top.inElse) throw new TemplateSyntaxError("stray_else", open);
      top.inElse = true;
    } else if (inner === "/if" || inner === "/unless") {
      const top = stack.pop();
      if (!top || `/${top.kind}` !== inner)
        throw new TemplateSyntaxError("unbalanced_block", open);
    } else if (PATH.test(inner)) {
      out().push({ t: "var", path: inner });
    } else {
      throw new TemplateSyntaxError("bad_tag", open);
    }
  }
  if (stack.length) throw new TemplateSyntaxError("unclosed_block", src.length);
  return root;
}

const ESC: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
export const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ESC[c]);

/** Own-property dot-path lookup (never walks the prototype chain). */
export function lookup(ctx: unknown, path: string): unknown {
  let cur: unknown = ctx;
  for (const key of path.split(".")) {
    if (cur === null || typeof cur !== "object" || !Object.hasOwn(cur, key))
      return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

const truthy = (v: unknown): boolean =>
  !(
    v === undefined ||
    v === null ||
    v === false ||
    v === "" ||
    v === 0 ||
    (Array.isArray(v) && v.length === 0)
  );

export type RenderResult = { output: string; missing: string[] };

export function renderTemplate(
  src: string,
  ctx: Record<string, unknown>,
  opts: { escape?: boolean } = {},
): RenderResult {
  const escape = opts.escape ?? true;
  const missing: string[] = [];
  // `prefix` is the output produced so far, used to detect href="…"/src="…" context.
  const walk = (nodes: TemplateNode[], prefix: string): string => {
    let out = "";
    for (const n of nodes) {
      if (n.t === "text") {
        out += n.v;
      } else if (n.t === "var") {
        const v = lookup(ctx, n.path);
        if (
          v === undefined ||
          v === null ||
          typeof v === "object" ||
          typeof v === "function"
        ) {
          if (!missing.includes(n.path)) missing.push(n.path);
          continue;
        }
        let s = String(v);
        const attr = escape ? urlAttrPrefix(prefix + out) : null;
        if (attr !== null) {
          if (SAFE_PREFIX.test(htmlTrim(attr))) {
            s = encodeFragment(s, htmlTrim(attr));
          } else if (!isSafeUrl(s)) {
            if (!missing.includes(n.path)) missing.push(n.path);
            continue;
          }
        }
        out += escape ? escapeHtml(s) : s;
      } else {
        out += walk(
          truthy(lookup(ctx, n.path)) !== n.neg ? n.then : n.else,
          prefix + out,
        );
      }
    }
    return out;
  };
  return { output: walk(parseTemplate(src), ""), missing };
}

/** C0/C1 control characters plus U+2028/U+2029 (line and paragraph separators). */
// eslint-disable-next-line no-control-regex
const HEADER_UNSAFE = /[\u0000-\u001f\u007f-\u009f\u{2028}\u{2029}]/gu;

/** Subject lines: no HTML escaping; CR/LF and other control characters are stripped (header injection). */
export function renderSubject(
  src: string,
  ctx: Record<string, unknown>,
): RenderResult {
  const r = renderTemplate(src, ctx, { escape: false });
  return { output: r.output.replace(HEADER_UNSAFE, ""), missing: r.missing };
}

/** Only https: and mailto: URLs may end up in href/src attributes. */
export function isSafeUrl(u: string): boolean {
  try {
    const p = new URL(u.trim()).protocol;
    return p === "https:" || p === "mailto:";
  } catch {
    return false;
  }
}

export function isHttpsUrl(u: string): boolean {
  try {
    return new URL(u.trim()).protocol === "https:";
  } catch {
    return false;
  }
}

/** Raw literal typed so far when the text ends inside a URL attribute value (quoted or unquoted), else null. */
function urlAttrPrefix(text: string): string | null {
  const open = tokenize(text).eof?.inAttrValue;
  return open && isUrlAttr(open.name) ? open.raw : null;
}

const SAFE_PREFIX = /^(?:https:\/\/|mailto:)/i;
const MAILTO_PREFIX = /^mailto:/i;
function encodeFragment(s: string, prefix: string): string {
  const e = encodeURIComponent(s);
  return MAILTO_PREFIX.test(prefix) ? e.replace(/%40/g, "@") : e;
}

export function extractVariables(src: string): string[] {
  const seen: string[] = [];
  const visit = (nodes: TemplateNode[]) => {
    for (const n of nodes) {
      if (n.t === "text") continue;
      if (!seen.includes(n.path)) seen.push(n.path);
      if (n.t === "if") {
        visit(n.then);
        visit(n.else);
      }
    }
  };
  visit(parseTemplate(src));
  return seen;
}

// Template tags are masked with same-length private-use characters before tokenizing, so they
// read as text; a block helper gets its own mark so it can be found inside attribute values.
const VAR_MARK = String.fromCharCode(0xe000);
const BLOCK_MARK = String.fromCharCode(0xe001);
const MARKS = new RegExp(`[${VAR_MARK}${BLOCK_MARK}]`);
const MARK_IN_TAG_SLOT = new RegExp(`</?[${VAR_MARK}${BLOCK_MARK}]`);
const BLOCK_TAG = /^\{\{\s*(?:[#/^]|else\s*\}\})/;
const maskTemplateTags = (src: string): string =>
  src.replace(/\{\{[^}]*\}\}/g, (m) =>
    (BLOCK_TAG.test(m) ? BLOCK_MARK : VAR_MARK).repeat(m.length),
  );

/**
 * Save-time validation, in order: block helper in a URL attribute, structural allowlist (every kind;
 * the `kind` option is kept for callers), template syntax, variable in an unquoted URL attribute, and a
 * URL attribute whose literal text before the first {{ is not empty, https:// or mailto:.
 */
export function validateTemplateSource(
  src: string,
  opts: { kind?: string; imagePrefixes?: readonly string[] } = {},
): { ok: true; variables: string[] } | { ok: false; code: string; at: number } {
  const masked = maskTemplateTags(src);
  const { tokens, eof } = tokenize(masked);
  const urlAttrs: { raw: string; quoted: boolean; at: number }[] =
    tokens.flatMap((t) =>
      t.type === "other" ? [] : t.attrs.filter((a) => isUrlAttr(a.name)),
    );
  if (eof?.inAttrValue && isUrlAttr(eof.inAttrValue.name))
    urlAttrs.push({ ...eof.inAttrValue, at: eof.at });
  const block = urlAttrs.find((a) => a.raw.includes(BLOCK_MARK));
  if (block) return { ok: false, code: "block_in_url_attr", at: block.at };
  const slot = MARK_IN_TAG_SLOT.exec(masked);
  if (slot) return { ok: false, code: "forbidden_tag", at: slot.index };
  const markup = markupAllowlistError(masked, {
    imagePrefixes: opts.imagePrefixes,
  });
  if (markup) return { ok: false, ...markup };
  try {
    const variables = extractVariables(src);
    const unquoted = urlAttrs.find((a) => !a.quoted && MARKS.test(a.raw));
    if (unquoted)
      return { ok: false, code: "unquoted_url_attr", at: unquoted.at };
    for (const a of urlAttrs) {
      const literal = htmlTrim(decodeAttr(a.raw).split(MARKS)[0]);
      if (literal && !SAFE_PREFIX.test(literal))
        return { ok: false, code: "unsafe_url_attr", at: a.at };
    }
    return { ok: true, variables };
  } catch (e) {
    if (e instanceof TemplateSyntaxError)
      return { ok: false, code: e.code, at: e.at };
    throw e;
  }
}
